import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, Clock, TrendingUp, Filter } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const Notifications = ({ user, onLogout }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    fetchNotifications();
  }, [page, typeFilter]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await api.getNotifications({
        page,
        page_size: pageSize,
        notification_type: typeFilter || undefined
      });
      setNotifications(res.data.notifications || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'milestone_achieved': return <CheckCircle className="text-green-600" size={24} />;
      case 'needs_attention': return <AlertTriangle className="text-yellow-600" size={24} />;
      case 'high_risk_alert': return <AlertTriangle className="text-red-600" size={24} />;
      case 'follow_up_reminder': return <Clock className="text-blue-600" size={24} />;
      default: return <Bell className="text-gray-600" size={24} />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'milestone_achieved': return 'border-l-green-500 bg-green-50';
      case 'needs_attention': return 'border-l-yellow-500 bg-yellow-50';
      case 'high_risk_alert': return 'border-l-red-500 bg-red-50';
      case 'follow_up_reminder': return 'border-l-blue-500 bg-blue-50';
      default: return 'border-l-gray-500 bg-gray-50';
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const formatType = (type) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
      <div className="min-h-screen bg-[#FAFAF9]" data-testid="notifications-page">
        <Navbar user={user} onLogout={onLogout} />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 text-[#0F4C81] font-semibold mb-4 hover:underline"
              data-testid="back-button"
            >
              <ChevronLeft size={20} />
              <span>{t('common.back')}</span>
            </button>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#111827] mb-2 font-['Nunito']" data-testid="page-title">
                  {t('notifications.title')}
                </h1>
                <p className="text-lg text-[#4B5563]">
                  {t('notifications.description')}
                </p>
              </div>
              <div className="flex items-center space-x-2 bg-[#0F4C81] text-white px-4 py-2 rounded-full">
                <Bell size={20} />
                <span className="font-bold">{total}</span>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-4 mb-6 shadow-sm">
            <div className="flex items-center space-x-4">
              <Filter size={20} className="text-[#4B5563]" />
              <select
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                className="h-10 px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none min-w-[200px]"
                data-testid="type-filter"
              >
                <option value="">{t('notifications.allTypes')}</option>
                <option value="milestone_achieved">{t('notifications.milestoneAchieved')}</option>
                <option value="needs_attention">{t('notifications.needsAttention')}</option>
                <option value="high_risk_alert">{t('notifications.highRiskAlert')}</option>
                <option value="follow_up_reminder">{t('notifications.followUpReminder')}</option>
              </select>
            </div>
          </div>

          {/* Notifications List */}
          <div className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center h-64 bg-white rounded-2xl border-2 border-slate-100">
                <div className="text-xl font-bold text-[#0F4C81]">{t('common.loading')}</div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 bg-white rounded-2xl border-2 border-slate-100 text-gray-500">
                <Bell size={48} className="mb-4 text-gray-300" />
                <p>{t('notifications.noNotifications')}</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`rounded-2xl border-l-4 p-6 shadow-sm ${getNotificationColor(notification.notification_type)}`}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.notification_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-[#111827] text-lg truncate pr-4">
                          {notification.title}
                        </h3>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <span className={`text-xs px-3 py-1 rounded-full font-bold ${getPriorityBadge(notification.priority)}`}>
                            {notification.priority?.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-[#4B5563] mb-3">{notification.message}</p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <button
                          onClick={() => navigate(`/child/${notification.child_id}`)}
                          className="flex items-center space-x-1 text-[#0F4C81] font-semibold hover:underline"
                        >
                          <TrendingUp size={16} />
                          <span>{notification.child_name}</span>
                        </button>
                        
                        <span className="text-[#9CA3AF]">
                          {new Date(notification.created_at).toLocaleString()}
                        </span>
                        
                        <span className="text-xs bg-[#E5E7EB] px-2 py-1 rounded-full">
                          {formatType(notification.notification_type)}
                        </span>
                        
                        <span className="text-[#9CA3AF]">
                          {Object.values(notification.sent_status).filter(Boolean).length}/{Object.keys(notification.sent_status).length} {t('notifications.emailsSent')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center justify-between mt-6 bg-white rounded-2xl border-2 border-slate-100 p-4">
              <p className="text-sm text-[#4B5563]">
                {t('notifications.showing', { start: (page - 1) * pageSize + 1, end: Math.min(page * pageSize, total), total })}
              </p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg border-2 border-slate-200 hover:border-[#0F4C81] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="prev-page"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="px-4 py-2 font-semibold text-[#111827]">
                  {page} / {totalPages || 1}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-2 rounded-lg border-2 border-slate-200 hover:border-[#0F4C81] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="next-page"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Copyright Footer */}
      <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
        <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
      </footer>
    </>
  );
};

export default Notifications;
